// Goal order for the Goals page and the ways the Tasks page lists tasks. Every active goal is listed,
// including goals with no tasks yet: an empty goal still needs ranking.

// Preference (21 Oct 2026, Craig: "I'd like to experiment with that to see if it's insightful") is the order
// the user would like to work on goals in. Only the Goals page shows it; Radar and the seeding never read it.
export const GOAL_VIEWS = { URGENCY: "goal_urgency", IMPORTANCE: "goal_importance", PREFERENCE: "goal_preference" };
// The Goals page's last view: goals on a calendar by deadline. Not an order, so not in GOAL_VIEWS.
export const GOALS_CALENDAR_VIEW = "goal_calendar";
// The Goals page's first view: Radar's Urgent & Important Goals, worked out from the two orders, so it can't
// be dragged (Craig, 23 Oct 2026: "make it basically the same as 'Urgent & Important Goals'").
export const GOALS_TOP_VIEW = "goal_top";

// The settings key that remembers the Goals page's view (sortModes in data/settings). Until 21 Oct 2026 Goals
// and Tasks were one page, so it can still hold a task view; goalsPageView reads that as Importance.
export const GROUP_BY_SETTING = "tasks_page_groupby";
export const goalsPageView = (stored) =>
  [...Object.values(GOAL_VIEWS), GOALS_CALENDAR_VIEW, GOALS_TOP_VIEW].includes(stored) ? stored : GOAL_VIEWS.IMPORTANCE;

// The Tasks page's views, and the settings key that remembers which is open. A first visit opens the task
// view last chosen on the old combined page, if there was one.
export const TASK_VIEWS = { ALL: "all_by_importance", DEADLINES: "deadlines", CATEGORY: "category", OLDEST: "procrastinating" };
export const TASK_VIEW_SETTING = "tasks_view";
export const tasksPageView = (stored, legacy) =>
  [stored, legacy].find(v => Object.values(TASK_VIEWS).includes(v)) || TASK_VIEWS.DEADLINES;

const rankFor = (goal, view) => {
  if (view === GOAL_VIEWS.URGENCY) return (goal.goalUrgencyOrder ?? goal.goalManualOrder) ?? Infinity;
  if (view === GOAL_VIEWS.IMPORTANCE) return goal.goalImportanceOrder ?? Infinity;
  if (view === GOAL_VIEWS.PREFERENCE) return goal.goalPreferenceOrder ?? Infinity;
  return Infinity;
};

// A goal's own tasks: the order the user set, then oldest first. Goal detail and the add-task sheet's
// "next task" both use this, so they agree on which task comes first.
export const goalTaskSort = (a, b) => {
  const ao = a.goalTaskOrder ?? Infinity, bo = b.goalTaskOrder ?? Infinity;
  if (ao !== bo) return ao - bo;
  return (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0);
};

// A goal's tasks in the order the goal shows them: the one join from goal to tasks (Today's add sheet,
// Help me choose, goal detail's Your order, and the Goals page and Radar, which list them under each goal; Craig, 5 Nov
// 2026, linked the Goals page's order to Your order).
export const tasksForGoal = (tasks, goalId) => (tasks || []).filter(t => t.parentGoalId === goalId).sort(goalTaskSort);

// What a task added under a goal starts with: the goal, its category, and the place after the goal's last
// unfinished task. Goal detail and the Goals page both add tasks this way.
export const newTaskForGoal = (goal, tasks) => ({
  parentGoalId: goal.id,
  categoryId: goal.categoryId || null,
  goalTaskOrder: tasksForGoal(tasks, goal.id).filter(t => !t.done).reduce((max, t) => Math.max(max, t.goalTaskOrder ?? 0), 0) + 1,
});

// Whether the user has placed any goal by preference. Until then Preference is the importance order.
export const placedByPreference = (goals) => (goals || []).some(g => g.goalPreferenceOrder != null);

// The orders on the Goals page's switch, which adds Top before them and Calendar after.
export const RANK_VIEW_OPTIONS = [[GOAL_VIEWS.IMPORTANCE, "Importance"], [GOAL_VIEWS.URGENCY, "Urgency"], [GOAL_VIEWS.PREFERENCE, "Preference"]];

// Ranked goals first; unranked by deadline (undated last), then by title. Goals not yet placed by preference
// start in importance order, so the first drag shows where the two differ.
export const sortGoalsForView = (goals, view) =>
  goals
    .filter(g => g.status === "active")
    .sort((a, b) => {
      const ra = rankFor(a, view), rb = rankFor(b, view);
      if (ra !== rb) return ra - rb;
      if (view === GOAL_VIEWS.PREFERENCE) {
        const ia = rankFor(a, GOAL_VIEWS.IMPORTANCE), ib = rankFor(b, GOAL_VIEWS.IMPORTANCE);
        if (ia !== ib) return ia - ib;
      }
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return (a.title || "").localeCompare(b.title || "");
    });

// The goals a picker offers (the task editor's and the Key Date editor's "Goal"): active goals, most urgent
// first, plus the goal already chosen even if it is no longer active, so opening a record never drops its link.
export const goalChoices = (goals, currentId = null) =>
  (goals || []).filter(g => g.status === "active" || g.id === currentId)
    .sort((a, b) => rankFor(a, GOAL_VIEWS.URGENCY) - rankFor(b, GOAL_VIEWS.URGENCY));

// Tasks by deadline, soonest first, undated last; then by their goal's urgency; then newest first.
export const byDeadline = (goals) => {
  const urgency = new Map((goals || []).map(g => [g.id, rankFor(g, GOAL_VIEWS.URGENCY)]));
  return (a, b) => {
    if (a.deadline && b.deadline && a.deadline !== b.deadline) return a.deadline.localeCompare(b.deadline);
    if (a.deadline && !b.deadline) return -1;
    if (!a.deadline && b.deadline) return 1;
    const ua = urgency.get(a.parentGoalId) ?? Infinity, ub = urgency.get(b.parentGoalId) ?? Infinity;
    if (ua !== ub) return ua - ub;
    return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
  };
};

// The goal an editor starts on: the first of `ids` that is still one of its `options` (goalChoices), or "" for
// none. A link to a goal that no longer exists counts as none, so Save never writes it back.
export const startingGoalId = (options, ...ids) => ids.find(id => id && options.some(g => g.id === id)) || "";
